if (typeof VideoCarouselRow !== 'function') {
	class VideoCarouselRow extends HTMLElement {
		constructor() {
			super();

			this.activeCard = null;
			this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			this.observer = null;

			this.handleCardMouseEnter = this.handleCardMouseEnter.bind(this);
			this.handleCardMouseLeave = this.handleCardMouseLeave.bind(this);
			this.handleCardFocusIn = this.handleCardFocusIn.bind(this);
			this.handleCardFocusOut = this.handleCardFocusOut.bind(this);
			this.handleOverlayPlay = this.handleOverlayPlay.bind(this);
			this.handleOverlayPause = this.handleOverlayPause.bind(this);
			this.handleMuteToggle = this.handleMuteToggle.bind(this);
			this.handleMutations = this.handleMutations.bind(this);
			this.handleMobileLayoutChange = this.handleMobileLayoutChange.bind(this);
		}

		connectedCallback() {
			this.updatePlayMode();
			this.bindCards();

			this.mobileLayoutQuery = window.matchMedia('(max-width: 767px)');
			this.mobileLayoutQuery.addEventListener('change', this.handleMobileLayoutChange);

			this.observer = new MutationObserver(this.handleMutations);
			this.observer.observe(this, { childList: true, subtree: true });
		}

		disconnectedCallback() {
			this.stopActiveCard();

			if (this.mobileLayoutQuery) {
				this.mobileLayoutQuery.removeEventListener('change', this.handleMobileLayoutChange);
				this.mobileLayoutQuery = null;
			}

			if (this.observer) {
				this.observer.disconnect();
				this.observer = null;
			}

			this.unbindCards();
		}

		get isMobileLayout() {
			return window.matchMedia('(max-width: 767px)').matches;
		}

		get supportsHover() {
			return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
		}

		get showPlayOverlay() {
			return this.dataset.showPlayOverlay !== 'false';
		}

		get useOverlayPlay() {
			return (
				this.dataset.playOnHover === 'true' &&
				this.showPlayOverlay &&
				!this.reducedMotion &&
				(!this.supportsHover || this.isMobileLayout)
			);
		}

		get playOnHover() {
			return (
				this.dataset.playOnHover === 'true' &&
				!this.reducedMotion &&
				this.supportsHover &&
				!this.isMobileLayout
			);
		}

		get playOnTap() {
			return this.useOverlayPlay;
		}

		updatePlayMode() {
			if (this.useOverlayPlay) {
				this.setAttribute('data-touch-play', 'true');
			} else {
				this.removeAttribute('data-touch-play');
			}
		}

		handleMobileLayoutChange() {
			this.stopActiveCard();
			this.updatePlayMode();
			this.unbindCards();
			this.bindCards();
		}

		getCards() {
			return Array.from(this.querySelectorAll('.video-carousel-card'));
		}

		bindCards(scope) {
			const cards =
				scope instanceof Element && scope.matches('.video-carousel-card')
					? [scope]
					: scope instanceof Element
						? Array.from(scope.querySelectorAll('.video-carousel-card'))
						: this.getCards();

			cards.forEach((card) => {
				if (card.__videoCarouselBound) {
					return;
				}

				if (this.playOnHover) {
					card.addEventListener('mouseenter', this.handleCardMouseEnter);
					card.addEventListener('mouseleave', this.handleCardMouseLeave);
					card.addEventListener('focusin', this.handleCardFocusIn);
					card.addEventListener('focusout', this.handleCardFocusOut);
				}

				if (this.playOnTap) {
					const playButton = card.querySelector('[data-video-carousel-play]');
					const pauseButton = card.querySelector('[data-video-carousel-pause]');
					const youtubePauseButton = card.querySelector('[data-video-carousel-youtube-pause]');

					if (playButton) {
						playButton.addEventListener('click', this.handleOverlayPlay);
					}

					if (pauseButton) {
						pauseButton.addEventListener('click', this.handleOverlayPause);
					}

					if (youtubePauseButton) {
						youtubePauseButton.addEventListener('click', this.handleOverlayPause);
					}
				}

				if (!card.classList.contains('video-carousel-card--poster-only')) {
					card.querySelector('[data-video-carousel-mute]')?.addEventListener('click', this.handleMuteToggle);
				}

				card.__videoCarouselBound = true;
			});
		}

		unbindCards() {
			this.getCards().forEach((card) => {
				card.removeEventListener('mouseenter', this.handleCardMouseEnter);
				card.removeEventListener('mouseleave', this.handleCardMouseLeave);
				card.removeEventListener('focusin', this.handleCardFocusIn);
				card.removeEventListener('focusout', this.handleCardFocusOut);

				card.querySelector('[data-video-carousel-play]')?.removeEventListener('click', this.handleOverlayPlay);
				card.querySelector('[data-video-carousel-pause]')?.removeEventListener('click', this.handleOverlayPause);
				card.querySelector('[data-video-carousel-youtube-pause]')?.removeEventListener('click', this.handleOverlayPause);
				card.querySelector('[data-video-carousel-mute]')?.removeEventListener('click', this.handleMuteToggle);

				delete card.__videoCarouselBound;
				delete card.__videoPaused;
				delete card.__audioMuted;
			});
		}

		handleMutations(mutations) {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (!(node instanceof Element)) {
						return;
					}

					this.bindCards(node);
				});
			});
		}

		handleCardMouseEnter(event) {
			if (!this.playOnHover) {
				return;
			}

			const card = event.currentTarget;
			this.playCard(card);
		}

		handleCardMouseLeave(event) {
			if (!this.playOnHover) {
				return;
			}

			const card = event.currentTarget;

			if (this.activeCard === card) {
				this.pauseCard(card);
			}
		}

		handleCardFocusIn(event) {
			if (!this.playOnHover) {
				return;
			}

			this.playCard(event.currentTarget);
		}

		handleCardFocusOut(event) {
			if (!this.playOnHover) {
				return;
			}

			const card = event.currentTarget;
			const relatedTarget = event.relatedTarget;

			if (relatedTarget && card.contains(relatedTarget)) {
				return;
			}

			if (this.activeCard === card) {
				this.pauseCard(card);
			}
		}

		handleOverlayPlay(event) {
			if (!this.playOnTap) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			this.playCard(event.currentTarget.closest('.video-carousel-card'));
		}

		handleOverlayPause(event) {
			if (!this.playOnTap) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const card = event.currentTarget.closest('.video-carousel-card');

			if (card?.classList.contains('video-carousel-card--playing') || card?.__videoPaused) {
				this.pauseCard(card);
			}
		}

		handleMuteToggle(event) {
			event.preventDefault();
			event.stopPropagation();

			const card = event.currentTarget.closest('.video-carousel-card');

			if (!card?.classList.contains('video-carousel-card--playing')) {
				return;
			}

			card.__audioMuted = card.__audioMuted === false;
			this.setMediaMuted(card, card.__audioMuted);
			this.updateMuteButton(card);
		}

		get scrollingText() {
			return this.querySelector('scrolling-text');
		}

		pauseMarquee() {
			if (this.dataset.autoSlide !== 'true') {
				return;
			}

			this.scrollingText?.dispatchEvent(
				new CustomEvent('on:scrolling-text:pause', {
					detail: { reason: 'video' },
				}),
			);
		}

		resumeMarquee({ force = false } = {}) {
			if (this.dataset.autoSlide !== 'true') {
				return;
			}

			this.scrollingText?.dispatchEvent(
				new CustomEvent('on:scrolling-text:resume', {
					detail: { reason: 'video', force },
				}),
			);
		}

		setOverlayState(card, state) {
			const playButton = card.querySelector('[data-video-carousel-play]');
			const pauseButton = card.querySelector('[data-video-carousel-pause]');
			const youtubePauseButton = card.querySelector('[data-video-carousel-youtube-pause]');
			const isYoutube = card.dataset.videoType === 'youtube';

			if (!playButton) {
				return;
			}

			playButton.hidden = state !== 'idle';

			if (isYoutube && youtubePauseButton) {
				youtubePauseButton.hidden = state !== 'playing' || !this.playOnTap;
			} else if (pauseButton) {
				pauseButton.hidden = state !== 'playing';
			}

			this.updateMuteButton(card, state);
		}

		updateMuteButton(card, state = null) {
			const muteButton = card.querySelector('[data-video-carousel-mute]');

			if (!muteButton) {
				return;
			}

			const playbackState =
				state || (card.classList.contains('video-carousel-card--playing') ? 'playing' : 'idle');
			const isMuted = card.__audioMuted !== false;
			const label = muteButton.querySelector('[data-video-carousel-mute-label]');
			const hideOnDesktopYoutube =
				!this.isMobileLayout && card.dataset.videoType === 'youtube';

			muteButton.hidden = playbackState !== 'playing' || hideOnDesktopYoutube;
			muteButton.setAttribute('aria-pressed', isMuted ? 'true' : 'false');

			if (label) {
				label.textContent = isMuted ? muteButton.dataset.unmuteLabel : muteButton.dataset.muteLabel;
			}
		}

		getMediaKey(card) {
			if (!card) {
				return '';
			}

			if (card.dataset.videoType === 'video') {
				return `video:${card.dataset.videoSources || ''}`;
			}

			return `${card.dataset.videoType || ''}:${card.dataset.videoId || ''}`;
		}

		playCard(card) {
			if (!card || card.classList.contains('video-carousel-card--poster-only')) {
				return;
			}

			this.getCards().forEach((otherCard) => {
				if (otherCard === card) {
					return;
				}

				const otherPlayer = otherCard.querySelector('.video-carousel-card__player');

				if (otherPlayer?.firstElementChild) {
					this.stopCard(otherCard);
				}
			});

			this.activeCard = card;
			const currentPlayer = card.querySelector('.video-carousel-card__player');
			const isResumingPausedVideo = Boolean(currentPlayer?.firstElementChild && card.__videoPaused);

			if (!isResumingPausedVideo) {
				card.__audioMuted = true;
			}

			this.pauseMarquee();

			if (!currentPlayer) {
				return;
			}

			if (isResumingPausedVideo) {
				card.__videoPaused = false;
				card.__playGeneration = (card.__playGeneration || 0) + 1;
				currentPlayer.hidden = false;
				currentPlayer.classList.remove('video-carousel-card__player--hidden');
				card.classList.remove('video-carousel-card--loading');
				card.classList.add('video-carousel-card--playing');
				this.setLoaderState(card, false);
				this.setOverlayState(card, 'playing');
				this.playMedia(card);

				return;
			}

			card.__playGeneration = (card.__playGeneration || 0) + 1;
			this.setOverlayState(card, 'loading');

			card.classList.remove('video-carousel-card--playing');
			card.classList.add('video-carousel-card--loading');
			card.__loaderStartedAt = Date.now();
			this.setLoaderState(card, true);
			this.preparePlayer(currentPlayer);

			if (!currentPlayer.firstElementChild) {
				const media = this.createPlayer(card);

				if (media) {
					currentPlayer.appendChild(media);
					this.bindPlayerReady(card, media, card.__playGeneration);
				} else {
					this.stopCard(card);
				}
			} else {
				this.showPlayer(card, card.__playGeneration);
			}
		}

		stopActiveCard() {
			if (this.activeCard) {
				this.stopCard(this.activeCard);
			}
		}

		pauseCard(card) {
			if (!card) {
				return;
			}

			card.__playGeneration = (card.__playGeneration || 0) + 1;
			this.clearReadyListeners(card);
			this.pauseMedia(card);
			card.__videoPaused = true;

			const player = card.querySelector('.video-carousel-card__player');

			if (player) {
				player.hidden = false;
				player.classList.add('video-carousel-card__player--hidden');
			}

			card.classList.remove('video-carousel-card--playing', 'video-carousel-card--loading');
			this.setLoaderState(card, false);
			this.setOverlayState(card, 'idle');

			if (this.activeCard === card) {
				this.activeCard = null;
			}

			this.resumeMarquee({ force: true });
		}

		stopCard(card) {
			if (!card) {
				return;
			}

			card.__playGeneration = (card.__playGeneration || 0) + 1;
			this.clearReadyListeners(card);
			this.pauseMedia(card);
			delete card.__videoPaused;
			delete card.__audioMuted;
			card.classList.remove('video-carousel-card--playing', 'video-carousel-card--loading');
			this.setLoaderState(card, false);
			this.setOverlayState(card, 'idle');

			const player = card.querySelector('.video-carousel-card__player');

			if (player) {
				player.innerHTML = '';
				player.hidden = true;
				player.classList.remove('video-carousel-card__player--hidden');
			}

			if (this.activeCard === card) {
				this.activeCard = null;
			}

			this.resumeMarquee({ force: true });
		}

		preparePlayer(player) {
			player.hidden = false;
			player.classList.add('video-carousel-card__player--hidden');
		}

		setLoaderState(card, isLoading) {
			const loader = card.querySelector('.video-carousel-card__loader');

			if (!loader) {
				return;
			}

			loader.hidden = !isLoading;
			loader.classList.toggle('video-carousel-card__loader--active', isLoading);
			loader.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
		}

		clearReadyListeners(card) {
			if (card.__readyAbortController) {
				card.__readyAbortController.abort();
				delete card.__readyAbortController;
			}

			if (card.__readyMessageHandler) {
				window.removeEventListener('message', card.__readyMessageHandler);
				delete card.__readyMessageHandler;
			}

			if (card.__readyTimeoutId) {
				window.clearTimeout(card.__readyTimeoutId);
				delete card.__readyTimeoutId;
			}
		}

		scheduleShowPlayer(card, generation, onReady) {
			const minLoaderMs = 300;
			const elapsed = Date.now() - (card.__loaderStartedAt || 0);
			const delay = Math.max(0, minLoaderMs - elapsed);

			if (card.__readyTimeoutId) {
				window.clearTimeout(card.__readyTimeoutId);
			}

			card.__readyTimeoutId = window.setTimeout(onReady, delay);
		}

		showPlayer(card, generation) {
			if (card.__playGeneration !== generation || this.activeCard !== card) {
				return;
			}

			const player = card.querySelector('.video-carousel-card__player');

			if (!player || !player.firstElementChild) {
				return;
			}

			this.clearReadyListeners(card);
			player.classList.remove('video-carousel-card__player--hidden');
			card.classList.remove('video-carousel-card--loading');
			this.setLoaderState(card, false);
			card.classList.add('video-carousel-card--playing');
			this.setOverlayState(card, 'playing');
			this.playMedia(card);
		}

		bindPlayerReady(card, media, generation) {
			if (!media) {
				return;
			}

			const onReady = () => {
				this.showPlayer(card, generation);
			};

			if (media.tagName === 'VIDEO') {
				this.bindVideoReady(card, media, generation, onReady);
				return;
			}

			if (media.tagName === 'IFRAME') {
				this.bindIframeReady(card, media, generation, onReady);
			}
		}

		bindVideoReady(card, video, generation, onReady) {
			let resolved = false;
			const abortController = new AbortController();

			card.__readyAbortController = abortController;

			const finish = () => {
				if (resolved || card.__playGeneration !== generation || this.activeCard !== card) {
					return;
				}

				resolved = true;
				this.clearReadyListeners(card);
				this.scheduleShowPlayer(card, generation, onReady);
			};

			video.addEventListener('playing', finish, { once: true, signal: abortController.signal });
			video.addEventListener(
				'waiting',
				() => {
					if (resolved || this.activeCard !== card) {
						return;
					}

					card.classList.add('video-carousel-card--loading');
					this.setLoaderState(card, true);
				},
				{ signal: abortController.signal },
			);

			if (video.readyState < 2) {
				video.load();
			}

			video.play().catch(() => {});

			card.__readyTimeoutId = window.setTimeout(finish, 500);
		}

		bindIframeReady(card, iframe, generation, onReady) {
			let resolved = false;

			const finish = () => {
				if (resolved || card.__playGeneration !== generation || this.activeCard !== card) {
					return;
				}

				resolved = true;
				this.clearReadyListeners(card);
				this.scheduleShowPlayer(card, generation, onReady);
			};

			const onMessage = (event) => {
				if (resolved || card.__playGeneration !== generation) {
					return;
				}

				if (!iframe.contentWindow || event.source !== iframe.contentWindow) {
					return;
				}

				let data = event.data;

				if (typeof data === 'string') {
					try {
						data = JSON.parse(data);
					} catch (error) {
						return;
					}
				}

				if (!data || typeof data !== 'object') {
					return;
				}

				if (data.event === 'onStateChange' && data.info === 1) {
					finish();
					return;
				}

				if (data.event === 'play' || data.method === 'play') {
					finish();
				}
			};

			card.__readyMessageHandler = onMessage;
			window.addEventListener('message', onMessage);

			iframe.addEventListener(
				'load',
				() => {
					if (iframe.classList.contains('js-youtube')) {
						iframe.contentWindow.postMessage('{"event":"listening","id":1}', '*');
					}
				},
				{ once: true },
			);

			card.__readyTimeoutId = window.setTimeout(finish, 500);
		}

		createPlayer(card) {
			const videoType = card.dataset.videoType;

			if (videoType === 'video') {
				return this.createShopifyVideo(card);
			}

			if (videoType === 'youtube') {
				return this.createIframe(this.getYoutubeEmbedUrl(card.dataset.videoId), 'js-youtube video-carousel-card__iframe');
			}

			if (videoType === 'vimeo') {
				return this.createIframe(
					`https://player.vimeo.com/video/${card.dataset.videoId}?api=1&autoplay=1&muted=1&controls=0&loop=1&playsinline=1`,
					'js-vimeo video-carousel-card__iframe',
				);
			}

			return null;
		}

		createShopifyVideo(card) {
			let sources = [];

			try {
				sources = JSON.parse(card.dataset.videoSources || '[]');
			} catch (error) {
				sources = [];
			}

			if (!sources.length) {
				return null;
			}

			const video = document.createElement('video');

			video.setAttribute('muted', '');
			video.setAttribute('loop', '');
			video.setAttribute('playsinline', '');
			video.setAttribute('webkit-playsinline', '');
			video.setAttribute('preload', 'none');
			video.className = 'video-carousel-card__video';
			video.muted = true;
			video.defaultMuted = true;
			video.loop = true;
			video.autoplay = true;
			video.playsInline = true;

			sources.forEach((source) => {
				const sourceEl = document.createElement('source');

				sourceEl.src = source.url;
				sourceEl.type = source.type;
				video.appendChild(sourceEl);
			});

			return video;
		}

		createIframe(src, className) {
			const iframe = document.createElement('iframe');

			iframe.src = src;
			iframe.className = className;
			iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
			iframe.setAttribute('allowfullscreen', '');
			iframe.setAttribute('tabindex', '-1');
			iframe.setAttribute('title', this.activeCard?.getAttribute('aria-label') || 'Video');

			return iframe;
		}

		getYoutubeEmbedUrl(videoId) {
			const useNativeControls = !this.isMobileLayout;
			const params = new URLSearchParams({
				enablejsapi: '1',
				autoplay: '1',
				mute: '1',
				controls: useNativeControls ? '1' : '0',
				loop: '1',
				playlist: videoId,
				playsinline: '1',
				rel: '0',
				modestbranding: '1',
			});

			return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
		}

		playMedia(card) {
			const isMuted = card.__audioMuted !== false;

			this.setMediaMuted(card, isMuted);

			const videoType = card.dataset.videoType;

			if (videoType === 'video') {
				const video = card.querySelector('video');

				if (video) {
					video.playsInline = true;
					video.play().catch(() => {});
				}

				return;
			}

			if (videoType === 'youtube') {
				const iframe = card.querySelector('.js-youtube');

				if (iframe) {
					iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
				}

				return;
			}

			if (videoType === 'vimeo') {
				const iframe = card.querySelector('.js-vimeo');

				if (iframe) {
					iframe.contentWindow.postMessage('{"method":"play"}', '*');
				}
			}
		}

		setMediaMuted(card, muted) {
			const videoType = card.dataset.videoType;

			if (videoType === 'video') {
				const video = card.querySelector('video');

				if (video) {
					video.muted = muted;
					video.defaultMuted = muted;
				}

				return;
			}

			if (videoType === 'youtube') {
				const iframe = card.querySelector('.js-youtube');

				if (iframe) {
					iframe.contentWindow.postMessage(
						`{"event":"command","func":"${muted ? 'mute' : 'unMute'}","args":""}`,
						'*',
					);
				}

				return;
			}

			if (videoType === 'vimeo') {
				const iframe = card.querySelector('.js-vimeo');

				if (iframe) {
					iframe.contentWindow.postMessage(`{"method":"setVolume","value":${muted ? 0 : 1}}`, '*');
				}
			}
		}

		pauseMedia(card) {
			const videoType = card.dataset.videoType;

			if (videoType === 'video') {
				const video = card.querySelector('video');

				if (video) {
					video.pause();
				}

				return;
			}

			if (videoType === 'youtube') {
				const iframe = card.querySelector('.js-youtube');

				if (iframe) {
					iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
				}

				return;
			}

			if (videoType === 'vimeo') {
				const iframe = card.querySelector('.js-vimeo');

				if (iframe) {
					iframe.contentWindow.postMessage('{"method":"pause"}', '*');
				}
			}
		}
	}

	if (typeof customElements.get('video-carousel-row') === 'undefined') {
		customElements.define('video-carousel-row', VideoCarouselRow);
	}
}
